import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getDuplicateThresholdRange,
  getSelectedQueryModal,
} from "redux/SingleDataset/selectors";
import {
  setSelectedQueryModal,
  updateSelectedQueryModal,
} from "redux/SingleDataset/actions";

import QueryModalTitle from "views/components/QueryModal/QueryModalTitle";
import ThresholdInput from "views/components/QueryModal/ThresholdInput";
import QueryModal from "views/components/QueryModal";
import {
  createAndUpdateDuplicatesFilter,
  isVqlQuery,
} from "helpers/utility/vqlHelpers";
import { Filters, isValidEntityType, VqlQuery } from "types";
import DupliacatesIcon from "assets/icons/DuplicateIcon";
import styles from "./style.module.scss";
import useVqlParams from "hooks/useVqlParams";
import { ENTITY_TYPE_PARAMETER } from "helpers/constants/miscellaneous";
import useAllQueryParamsFilters from "hooks/useAllQueryParamsFilters";

interface DuplicatesQueryModalProps {
  editQueryId?: string;
}

export default function DuplicatesQueryModal({
  editQueryId,
}: DuplicatesQueryModalProps) {
  const { findVqlQuery, addVqlQuery, updateVqlQuery, vqlQueries } =
    useVqlParams();
  const oldQuery = editQueryId ? findVqlQuery(editQueryId, "duplicates") : null;
  const selectedModalData = useSelector((state) =>
    getSelectedQueryModal(state),
  );
  const dispatch = useDispatch();

  const queryParamsFilters = useAllQueryParamsFilters();
  const entityTypeFromQueryParam = isValidEntityType(
    queryParamsFilters[ENTITY_TYPE_PARAMETER],
  )
    ? queryParamsFilters[ENTITY_TYPE_PARAMETER]
    : null;

  let isImageEntity =
    entityTypeFromQueryParam === null ||
    entityTypeFromQueryParam.toUpperCase() === "IMAGES";
  const duplicateThresholdRange = useSelector((state) =>
    getDuplicateThresholdRange(state),
  );
  const thresholdRange = isImageEntity
    ? (duplicateThresholdRange?.imagesDuplicateThresholds as number[])
    : (duplicateThresholdRange?.objectsDuplicateThresholds as number[]);

  const threshold =
    oldQuery?.duplicates?.value || thresholdRange[thresholdRange.length - 1];

  const modalRef = useRef<HTMLDivElement>(null);
  const submitQuery = (value: number) => {
    const vqlQuery = createAndUpdateDuplicatesFilter(value, editQueryId);
    editQueryId ? updateVqlQuery(vqlQuery) : addVqlQuery(vqlQuery);
    if (selectedModalData) {
      dispatch(
        setSelectedQueryModal({ ...selectedModalData, id: vqlQuery.id }),
      );
    }
  };

  const handleSubmission = (value: number) => {
    if (value === threshold) return;
    else submitQuery(value);
  };

  useEffect(() => {
    const query = vqlQueries.find((query: VqlQuery) =>
      isVqlQuery(query, "duplicates"),
    );
    if (query) {
      dispatch(
        updateSelectedQueryModal({ type: Filters.DUPLICATES, id: query.id }),
      );
    } else {
      submitQuery(threshold);
      dispatch(updateSelectedQueryModal({ type: Filters.DUPLICATES }));
    }
    // eslint-disable-next-line
  }, [editQueryId]);

  return (
    <QueryModal modalRef={modalRef} keepOpenOnLoading>
      <QueryModalTitle
        icon={<DupliacatesIcon />}
        info="Detects groups of identical or visually similar images. Adjust sensitivity to control how close the matches are."
      >
        Duplicates
      </QueryModalTitle>
      <div className={styles.filterContent}>
        <ThresholdInput
          title="Similarity Level:"
          value={threshold}
          range={thresholdRange}
          onChange={handleSubmission}
        />
      </div>
    </QueryModal>
  );
}
